// Aba "Previsões salvas" (27/10/2026): baseline e baseline ajustado de cada mês,
// com a data em que foram salvos, quem salvou, os totais e o detalhe por
// entregável. Versões sobrescritas ficam como histórico.
import { useEffect, useMemo, useState } from 'react';
import { App as AntApp, Card, Empty, Flex, Select, Spin, Table, Tag, Typography } from 'antd';
import * as api from './api';
import type { FotografiaSalva } from './api';
import { brl, dataBr, nomeDe, rotuloCompetencia, textoRateio } from './regras';

const { Text } = Typography;

const dataHora = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? dataBr(iso) : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function AbaPrevisoes() {
  const { message } = AntApp.useApp();
  const [ano, setAno] = useState(new Date().getFullYear());
  const [fotos, setFotos] = useState<FotografiaSalva[] | null>(null);
  useEffect(() => {
    setFotos(null);
    api.obterFotografias(ano).then(r => setFotos(r.fotografias)).catch(e => { message.error(api.erroDe(e)); setFotos([]); });
  }, [ano, message]);

  const meses = useMemo(() => {
    const porMes = new Map<string, FotografiaSalva[]>();
    for (const f of fotos || []) { const k = f.competencia.slice(0, 7); porMes.set(k, [...(porMes.get(k) || []), f]); }
    return Array.from(porMes.entries()).sort((a, b) => b[0].localeCompare(a[0])).map(([mes, lista]) => ({
      mes, baseline: lista.find(f => f.tipo === 'baseline' && !f.substituida_em) || null, ajuste: lista.find(f => f.tipo === 'ajuste' && !f.substituida_em) || null,
      antigas: lista.filter(f => f.substituida_em),
    }));
  }, [fotos]);

  const resumo = (f: FotografiaSalva | null) => f ? (
    <span>
      <Text strong>{brl(f.total_provavel)}</Text> <Text type="secondary">provável · {brl(f.total_possivel)} possível</Text>
      <br /><Text type="secondary" style={{ fontSize: 12 }}>salvo em {dataHora(f.salvo_em)} por {nomeDe(f.salvo_por)} · {f.itens.length} entregáveis</Text>
    </span>
  ) : <Text type="secondary">não salvo</Text>;

  return (
    <Spin spinning={fotos == null}>
      <Flex gap={12} align="center" style={{ marginBottom: 12 }}>
        <Select style={{ width: 110 }} value={ano} onChange={setAno} showSearch optionFilterProp="label"
          options={[ano - 1, ano, ano + 1].map(a => ({ value: a, label: String(a) }))} />
        <Text type="secondary">Baseline (início do mês) e baseline ajustado (por volta do dia 15) salvos nas abas Mês e Entregáveis. A versão em vigor é a referência dos indicadores.</Text>
      </Flex>
      {meses.length ? (
        <Table rowKey="mes" size="small" pagination={false} dataSource={meses}
          expandable={{
            expandedRowRender: m => (
              <Flex vertical gap={12}>
                {(['baseline', 'ajuste'] as const).map(t => m[t] && (
                  <Card key={t} size="small" title={`${t === 'baseline' ? 'Baseline' : 'Baseline ajustado'} — ${brl(m[t]!.total_provavel)} provável · por setor: ${textoRateio(Object.fromEntries(Object.entries(m[t]!.por_setor || {}).map(([k, v]) => [k, m[t]!.total_provavel ? Number(v) / m[t]!.total_provavel : 0])))}`}>
                    <Table rowKey={(i: any) => `${i.item_id}`} size="small" pagination={false} dataSource={m[t]!.itens}
                      columns={[
                        { title: 'Projeto', dataIndex: 'numero' },
                        { title: 'Entregável', dataIndex: 'descricao' },
                        { title: 'Previsto para', dataIndex: 'data_prevista', render: v => dataBr(v) },
                        { title: 'Possível', dataIndex: 'possivel', align: 'right', render: v => brl(v, 2) },
                        { title: 'Prob.', dataIndex: 'probabilidade', align: 'right', render: v => `${v}%` },
                        { title: 'Provável', dataIndex: 'provavel', align: 'right', render: v => brl(v, 2) },
                      ]} />
                  </Card>
                ))}
                {m.antigas.length > 0 && (
                  <Card size="small" title="Versões substituídas">
                    {m.antigas.map(f => (
                      <div key={f.id}><Tag>{f.tipo === 'baseline' ? 'baseline' : 'ajuste'}</Tag>{brl(f.total_provavel)} provável — salvo em {dataHora(f.salvo_em)} por {nomeDe(f.salvo_por)};
                        substituído em {dataHora(f.substituida_em)} por {nomeDe(f.substituida_por)}</div>
                    ))}
                  </Card>
                )}
              </Flex>
            ),
          }}
          columns={[
            { title: 'Mês', dataIndex: 'mes', render: v => <Text strong>{rotuloCompetencia(`${v}-01`)}</Text> },
            { title: 'Baseline', render: (_, m) => resumo(m.baseline) },
            { title: 'Baseline ajustado', render: (_, m) => resumo(m.ajuste) },
            { title: 'Variação ajuste × baseline', align: 'right', render: (_, m) => m.baseline && m.ajuste && m.baseline.total_provavel
              ? `${(((m.ajuste.total_provavel / m.baseline.total_provavel) - 1) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—' },
            { title: 'Versões antigas', align: 'right', render: (_, m) => (m.antigas.length ? <Tag>{m.antigas.length}</Tag> : '—') },
          ]} />
      ) : fotos && <Empty description={`Nenhuma previsão salva em ${ano}.`} />}
    </Spin>
  );
}
